/**
 * colorUtils.js
 *
 * Shared utility functions for color manipulation and accessibility.
 */

/**
 * Determines if text should be black or white for optimal contrast on a given background color.
 * Uses WCAG luminance formula for accessibility.
 * Supports both Hex (#ffffff, ffffff) and RGB (rgb(255, 255, 255)) formats.
 *
 * @param {string} color - Hex or RGB color string
 * @returns {string} '#1a1818' for light backgrounds, '#ffffff' for dark backgrounds
 */
export function getContrastColor(color) {
  if (!color) return '#ffffff';

  let r = 255;
  let g = 255;
  let b = 255;

  if (typeof color === 'string' && color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      r = parseInt(match[0], 10);
      g = parseInt(match[1], 10);
      b = parseInt(match[2], 10);
    }
  } else if (typeof color === 'string') {
    const hex = color.replace('#', '');
    if (hex.length >= 6) {
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    } else if (hex.length === 3) {
      r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
      g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
      b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    }
  }

  // Calculate luminance using standard WCAG formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return dark text for light backgrounds, white for dark
  return luminance > 0.5 ? '#1a1818' : '#ffffff';
}

/**
 * Converts hex color to RGB object
 *
 * @param {string} hexColor - Hex color code (with or without #)
 * @returns {object} {r, g, b} RGB values (0-255)
 */
export function hexToRgb(hexColor) {
  const hex = hexColor.replace('#', '');
  return {
    r: parseInt(hex.substr(0, 2), 16),
    g: parseInt(hex.substr(2, 2), 16),
    b: parseInt(hex.substr(4, 2), 16),
  };
}

/**
 * Converts RGB to hex color
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color code with #
 */
export function rgbToHex(r, g, b) {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}
